import { memo, useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormBuilderCardWrapper } from '../../../FormBuilderCardWrapper';
import { FormBuilderElement } from '../../../FormBuilderElement';
import {
  useFormBuilderFields,
  useFormBuilderFieldWithError,
} from '../../../hooks';
import {
  EFormBuilderFieldType,
  TFormBuilderSingleLineInputField,
} from '@/types';

type TProps = {
  field: TFormBuilderSingleLineInputField;
  index: number;
};

export const FormBuilderSingleLineInput =
  /**
   * Single line text input field component for form builder with editable label and placeholder
   * @param {TProps} props - Component props
   * @param {TFormBuilderSingleLineInputField} props.field - Single line input field data
   * @param {number} props.index - Field index in the form
   * @returns {JSX.Element} Form builder single line input field component
   */
  memo(({ field, index }: TProps) => {
    const { t } = useTranslation();
    const inputId = useId();
    const [active, setActive] = useState(false);
    const onActiveChange = useCallback(
      (value: boolean) => setActive(value),
      [],
    );
    const error =
      useFormBuilderFieldWithError<TFormBuilderSingleLineInputField>(index);

    const { changeFields, removeField, copyField, moveDownField, moveUpField } =
      useFormBuilderFields(field);

    return (
      <FormBuilderCardWrapper
        variant={EFormBuilderFieldType.SINGLE_LINE_INPUT}
        error={!!error}
        active={active}
        onActiveChange={onActiveChange}
        onRemove={removeField}
        onDownMove={moveDownField}
        onUpMove={moveUpField}
        onDuplicate={copyField}
      >
        <FormBuilderElement
          label={
            <FormBuilderElement.Label
              label={field.label}
              htmlFor={`${inputId}-input`}
              onLabelChange={(label) => changeFields({ label })}
              placeholder={t('form-builder.your-label-here')}
              required={field.required}
              error={!!error?.label}
            />
          }
          required={field.required}
          onRequiredChange={(required) => changeFields({ required })}
        >
          <FormBuilderElement.SingleLineInput
            id={inputId}
            placeholder={t('form-builder.placeholder-text')}
            value={field.placeholder}
            onChange={(e) => changeFields({ placeholder: e.target.value })}
          />
        </FormBuilderElement>
      </FormBuilderCardWrapper>
    );
  });

FormBuilderSingleLineInput.displayName = 'FormBuilderSingleLineInput';
